import React, { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader, HelpCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useSendRankingDataIncremental } from '../../../hooks/ranking/useSendRankingDataIncremental';
import { useAuth } from '../../../context/AuthContext';
import './styles.scss';

interface ImportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: (data: ImportResult) => void;
}

interface ImportResult {
  total: number;
  success: number;
  errors: number;
  data: any[];
  errorDetails?: string[];
  rowErrors?: RowError[];
}

interface ParsedRow {
  [key: string]: any;
}

interface ColumnMapping {
  name?: string;
  player?: string;
  email?: string;
  total?: string;
  team?: string;
  date?: string;
}

interface RowError {
  row: number;
  line: number; // linha real na planilha (row + 2 por causa do header)
  errors: string[];
  data?: any;
}

type PayloadField = 'name' | 'player' | 'email' | 'total' | 'team' | 'date';

const PAYLOAD_FIELDS: { key: PayloadField; label: string; required: boolean }[] = [
  { key: 'name', label: 'Nome', required: true },
  { key: 'player', label: 'ID (e-mail)', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'total', label: 'Total (Pontos)', required: true },
  { key: 'team', label: 'Time', required: false },
  { key: 'date', label: 'Data', required: false },
];

export const ImportDataModal: React.FC<ImportDataModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
}) => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<ParsedRow[]>([]);
  const [sheetColumns, setSheetColumns] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [dataType, setDataType] = useState<'INCREMENTAL' | 'CONSOLIDATED'>('INCREMENTAL');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [importStats, setImportStats] = useState<{ total: number; success: number; errors: number } | null>(null);
  const [step, setStep] = useState<'upload' | 'configure' | 'preview' | 'success' | 'error-report'>('upload');
  const [showTooltip, setShowTooltip] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingType, setPendingType] = useState<'INCREMENTAL' | 'CONSOLIDATED' | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendRankingDataIncremental = useSendRankingDataIncremental();
  const [sendEmail, setSendEmail] = useState(false);

  const handleDataTypeChange = (newType: 'INCREMENTAL' | 'CONSOLIDATED') => {
    if (dataType !== newType) {
      setPendingType(newType);
      setConfirmOpen(true);
    }
  };

  if (!isOpen) return null;

  const confirmChangeApply = () => {
    if (pendingType) setDataType(pendingType);
    setConfirmOpen(false);
    setPendingType(null);
  };

  const confirmChangeCancel = () => {
    setConfirmOpen(false);
    setPendingType(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];

    if (!hasValidExtension && !validTypes.includes(file.type)) {
      setValidationErrors(['❌ Formato de arquivo inválido. Por favor, selecione um arquivo .xlsx, .xls ou .csv']);
      return;
    }

    setSelectedFile(file);
    setValidationErrors([]);
    setRowErrors([]);
    parseFile(file);
  };

  const parseFile = (file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData: any[] = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          alert('Planilha vazia. Por favor, selecione um arquivo com dados.');
          return;
        }

        const columns = Object.keys(jsonData[0]);
        setSheetColumns(columns);
        setPreviewData(jsonData);
        setStep('configure');
        
        const autoMapping: ColumnMapping = {};
        columns.forEach(col => {
          const colLower = col.toLowerCase();
          if (colLower.includes('name') || colLower.includes('nome')) {
            autoMapping.name = col;
          }
          if (colLower.includes('player') || colLower.includes('jogador')) {
            autoMapping.player = col;
          }
          if (colLower.includes('email') || colLower.includes('e-mail')) {
            autoMapping.email = col;
          }
          if (colLower.includes('total') || colLower.includes('ponto') || colLower.includes('point')) {
            autoMapping.total = col;
          }
          if (colLower.includes('team') || colLower.includes('time') || colLower.includes('equipe')) {
            autoMapping.team = col;
          }
          if (colLower.includes('date') || colLower.includes('data')) {
            autoMapping.date = col;
          }
        });
        setColumnMapping(autoMapping);

      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        alert('Erro ao processar arquivo. Verifique se o formato está correto.');
      }
    };

    reader.readAsBinaryString(file);
  };

  const validateMapping = (): string[] => {
    const errors: string[] = [];
    const requiredFields = PAYLOAD_FIELDS.filter(f => f.required);
    
    requiredFields.forEach(field => {
      if (!columnMapping[field.key]) {
        errors.push(`Campo obrigatório "${field.label}" não foi mapeado`);
      }
    });

    return errors;
  };

  const isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const isValidNumber = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    return !isNaN(Number(value));
  };

  const validateRow = (row: any, rowIndex: number, seenIds?: Set<string>): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    const mappedValues = Object.keys(columnMapping)
      .filter(key => columnMapping[key as PayloadField])
      .map(key => {
        const colName = columnMapping[key as PayloadField];
        return colName ? row[colName] : null;
      });
    
    const isEmptyRow = mappedValues.every(val => val === null || val === undefined || val === '');
    if (isEmptyRow) {
      errors.push('Linha vazia ou sem dados válidos');
      return { isValid: false, errors };
    }

    // Validar ID duplicado (player)
    if (seenIds && columnMapping.player) {
      const playerId = row[columnMapping.player];
      if (playerId) {
        const playerIdStr = String(playerId).trim();
        if (seenIds.has(playerIdStr)) {
          errors.push(`ID duplicado: "${playerIdStr}" já foi processado anteriormente`);
          return { isValid: false, errors };
        }
      }
    }

    const requiredFields = PAYLOAD_FIELDS.filter(f => f.required);
    requiredFields.forEach(field => {
      const columnName = columnMapping[field.key];
      if (columnName) {
        const value = row[columnName];
        if (value === null || value === undefined || String(value).trim() === '') {
          errors.push(`Campo obrigatório "${field.label}" está vazio`);
        }
      }
    });

    if (columnMapping.email) {
      const emailValue = row[columnMapping.email];
      if (emailValue && !isValidEmail(String(emailValue))) {
        errors.push(`Email inválido: "${emailValue}"`);
      }
    }

    if (columnMapping.total) {
      const totalValue = row[columnMapping.total];
      if (totalValue !== null && totalValue !== undefined && totalValue !== '' && !isValidNumber(totalValue)) {
        errors.push(`Total deve ser um número válido. Valor encontrado: "${totalValue}"`);
      }
    }

    return { isValid: errors.length === 0, errors };
  };

  const handleConfigure = () => {
    const errors = validateMapping();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setStep('preview');
  };

  const handleImport = async () => {
    if (!selectedFile || previewData.length === 0 || !user) return;

    setIsProcessing(true);

    try {
      const validPlayers: any[] = [];
      const errors: RowError[] = [];
      const seenIds = new Set<string>(); // Rastrear IDs já processados

      previewData.forEach((row, index) => {
        const validation = validateRow(row, index, seenIds);
        
        if (!validation.isValid) {
          errors.push({
            row: index,
            line: index + 2, 
            errors: validation.errors,
            data: row,
          });
        } else {
          const mappedRow: any = {};
          
          if (columnMapping.name) mappedRow.name = String(row[columnMapping.name] || '').trim();
          if (columnMapping.player) {
            const playerId = String(row[columnMapping.player] || '').trim();
            mappedRow.player = playerId;
            // Adicionar ID ao conjunto de IDs vistos
            if (playerId) {
              seenIds.add(playerId);
            }
          }
          if (columnMapping.email) mappedRow.email = String(row[columnMapping.email] || '').trim();
          if (columnMapping.total) {
            const totalValue = row[columnMapping.total];
            mappedRow.total = typeof totalValue === 'number' ? totalValue : parseFloat(totalValue) || 0;
          }
          if (columnMapping.team) mappedRow.team = String(row[columnMapping.team] || '').trim();
          if (columnMapping.date) {
            const dateValue = row[columnMapping.date];
            if (dateValue) {
              mappedRow.date = dateValue instanceof Date ? dateValue : new Date(dateValue);
            }
          }

          validPlayers.push(mappedRow);
        }
      });

      setRowErrors(errors);
      setImportStats({
        total: previewData.length,
        success: validPlayers.length,
        errors: errors.length,
      });

      if (errors.length > 0) {
        setIsProcessing(false);
        setStep('error-report');
        return;
      }

      const payload = {
        type: dataType,
        sendInvite: sendEmail,
        user: user._id || user.email || '',
        players: validPlayers,
      };

      const response = await sendRankingDataIncremental(payload);

      const result: ImportResult = {
        total: previewData.length,
        success: validPlayers.length,
        errors: 0,
        data: validPlayers,
        rowErrors: [],
      };

      setIsProcessing(false);
      setStep('success');
      
      console.log('Modal de sucesso ativado - step:', 'success');
      
      setTimeout(() => {
        console.log('Fechando modal após 3 segundos');
        onImportSuccess(result); 
        handleClose();
      }, 3000);
      
    } catch (error) {
      console.error('Erro ao importar dados:', error);
      setValidationErrors(['Erro ao importar dados. Por favor, tente novamente.']);
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setSheetColumns([]);
    setColumnMapping({});
    setValidationErrors([]);
    setRowErrors([]);
    setImportStats(null);
    setIsProcessing(false);
    setStep('upload');
    setDataType('INCREMENTAL');
    setSendEmail(false);
    onClose();
  };

  const handleBack = () => {
    if (step === 'error-report') {
      setStep('preview');
      setRowErrors([]);
      setImportStats(null);
    } else if (step === 'preview') {
      setStep('configure');
    } else if (step === 'configure') {
      setStep('upload');
      setSelectedFile(null);
      setPreviewData([]);
      setSheetColumns([]);
      setColumnMapping({});
    }
  };

  const handleDownloadErrorReport = () => {
    if (rowErrors.length === 0) return;

    const reportLines = [
      '=== RELATÓRIO DE ERROS DE IMPORTAÇÃO ===\n',
      `Data: ${new Date().toLocaleString('pt-BR')}\n`,
      `Arquivo: ${selectedFile?.name}\n`,
      `Total de linhas: ${importStats?.total || 0}\n`,
      `Linhas com sucesso: ${importStats?.success || 0}\n`,
      `Linhas com erro: ${importStats?.errors || 0}\n`,
      '\n=== DETALHES DOS ERROS ===\n\n',
    ];

    rowErrors.forEach((error) => {
      reportLines.push(`📍 Linha ${error.line}:\n`);
      error.errors.forEach((err) => {
        reportLines.push(`   ❌ ${err}\n`);
      });
      if (error.data) {
        reportLines.push(`   📄 Dados da linha: ${JSON.stringify(error.data)}\n`);
      }
      reportLines.push('\n');
    });

    const blob = new Blob([reportLines.join('')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-erros-${new Date().getTime()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleIgnoreErrorsAndImport = async () => {
    // Importar apenas linhas válidas, ignorando erros
    if (!user) return;

    setIsProcessing(true);

    try {
      const validPlayers: any[] = [];
      const seenIds = new Set<string>(); // Rastrear IDs já processados

      previewData.forEach((row, index) => {
        const validation = validateRow(row, index, seenIds);
        
        if (validation.isValid) {
          const mappedRow: any = {};
          
          if (columnMapping.name) mappedRow.name = String(row[columnMapping.name] || '').trim();
          if (columnMapping.player) {
            const playerId = String(row[columnMapping.player] || '').trim();
            mappedRow.player = playerId;
            // Adicionar ID ao conjunto de IDs vistos
            if (playerId) {
              seenIds.add(playerId);
            }
          }
          if (columnMapping.email) mappedRow.email = String(row[columnMapping.email] || '').trim();
          if (columnMapping.total) {
            const totalValue = row[columnMapping.total];
            mappedRow.total = typeof totalValue === 'number' ? totalValue : parseFloat(totalValue) || 0;
          }
          if (columnMapping.team) mappedRow.team = String(row[columnMapping.team] || '').trim();
          if (columnMapping.date) {
            const dateValue = row[columnMapping.date];
            if (dateValue) {
              mappedRow.date = dateValue instanceof Date ? dateValue : new Date(dateValue);
            }
          }

          validPlayers.push(mappedRow);
        }
      });

      const payload = {
        type: dataType,
        sendInvite: sendEmail,
        user: user._id || user.email || '',
        players: validPlayers,
      };

      await sendRankingDataIncremental(payload);

      const result: ImportResult = {
        total: previewData.length,
        success: validPlayers.length,
        errors: rowErrors.length,
        data: validPlayers,
        rowErrors: rowErrors,
      };

      setIsProcessing(false);
      setStep('success');
      
      setTimeout(() => {
        onImportSuccess(result);
        handleClose();
      }, 3000);
      
    } catch (error) {
      console.error('Erro ao importar dados:', error);
      setValidationErrors(['Erro ao importar dados. Por favor, tente novamente.']);
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
      handleFileSelect({ target: fileInputRef.current } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div className="import-modal-overlay">
      <div className="import-modal-container">
        <div className="import-modal-header">
          {step === 'success' ? (
            <>
              <div className="w-full flex flex-col items-center justify-center gap-6 py-16">
                <div className="flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 shadow-xl animate-pulse">
                  <CheckCircle2 size={56} className="text-white" />
                </div>
                <h3 className="text-3xl font-bold text-white">Importação Concluída</h3>
                <p className="text-slate-300 text-center max-w-md text-lg">A importação foi concluída com sucesso!</p>
              </div>
              <button className="import-modal-close" onClick={handleClose} disabled={isProcessing}>
                <X size={24} />
              </button>
            </>
          ) : (
            <>
              <h2 className="import-modal-title">
                {step === 'upload' && 'Importar Dados de Planilha'}
                {step === 'configure' && 'Mapeamento de Campos'}
                {step === 'preview' && 'Pré-visualização'}
                {step === 'error-report' && 'Relatório de Erros'}
              </h2>
              <div className="header-actions">
                {step === 'configure' && (
              <div className="tooltip-container">
                <button
                  className="help-button"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onClick={() => setShowTooltip(!showTooltip)}
                >
                  <HelpCircle size={20} />
                  <span>Como usar?</span>
                </button>
                {showTooltip && (
                  <div className="tooltip-content">
                    <h4>Como usar o Importador</h4>
                    <div className="tooltip-steps">
                      <div className="tooltip-step">
                        <span className="step-number">1</span>
                        <div className="step-content">
                          <strong>Selecione o tipo de envio:</strong>
                          <ul>
                            <li>
                              <strong>Incremental:</strong> Os novos registros serão <em className='text-red-600'>somados</em> aos dados anteriores.
                            </li>
                            <li>
                              <strong>Consolidado:</strong> Os dados anteriores serão <em className='text-red-600'>substituídos</em> completamente pelos novos.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="tooltip-step">
                        <span className="step-number">2</span>
                        <div className="step-content">
                          <strong>Mapeie os campos obrigatórios:</strong>
                          <p>Relacione cada campo do sistema com a coluna correspondente da sua planilha.</p>
                        </div>
                      </div>
                      <div className="tooltip-step">
                        <span className="step-number">3</span>
                        <div className="step-content">
                          <strong>Campos opcionais:</strong>
                          <p>Para campos extras ou não utilizados, selecione "Ignorar esse campo".</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button className="import-modal-close" onClick={handleClose} disabled={isProcessing}>
              <X size={24} />
            </button>
          </div>
          </>
          )}
        </div>

        {step !== 'success' && (
          <div className="import-modal-content">
            {step === 'upload' && (
            <>
              <div
                className="import-dropzone"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FileSpreadsheet size={48} className="dropzone-icon" />
                <p className="dropzone-text">
                  Arraste e solte seu arquivo aqui ou <span className="dropzone-link">clique para selecionar</span>
                </p>
                <p className="dropzone-subtitle">Formatos aceitos: .xlsx, .xls, .csv</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Exibir erro de formato inválido */}
              {validationErrors.length > 0 && (
                <div className="import-validation-errors mt-4">
                  <div className="errors-header">
                    <AlertCircle size={20} />
                    <span>Erro no arquivo</span>
                  </div>
                  <ul className="errors-list">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {step === 'configure' && (
            <div className="import-configure-section">
              <div className="configure-header">
                <h3>Mapeamento de Campos</h3>
                <p className='bg-[#8b5cf6] rounded-md p-1 flex text-white font-semibold'>Relacione as colunas da sua planilha com os campos do sistema</p>
                <p className='bg-[#8b5cf6] rounded-md p-1 flex text-white font-semibold'>Quando os dados forem importados, automaticamente os usuários receberão uma mensagem de convite via email.</p>
              </div>

              <div className="data-type-toggle">
                <label>Tipo de Envio:</label>
                <div className="toggle-buttons">
                  <button
                    className={dataType === 'INCREMENTAL' ? 'active' : ''}
                    onClick={() => handleDataTypeChange('INCREMENTAL')}
                  >
                    Incremental
                  </button>
                  <button
                    className={dataType === 'CONSOLIDATED' ? 'active' : ''}
                    onClick={() => handleDataTypeChange('CONSOLIDATED')}
                  >
                    Consolidado
                  </button>
                </div>
              </div>

              {/* Email Toggle Switch */}
              <div className="email-toggle-container">
                <label className="email-toggle-label">
                  <span className="email-toggle-text">Enviar e-mails para os usuários</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={() => setSendEmail(!sendEmail)}
                      className="toggle-input"
                    />
                    <span className="toggle-slider"></span>
                  </div>
                  <span className={`email-toggle-status ${sendEmail ? 'active' : 'inactive'}`}>
                    {sendEmail ? 'Enviar' : 'Não Enviar'}
                  </span>
                </label>
              </div>

              {validationErrors.length > 0 && (
                <div className="import-validation-errors">
                  <div className="errors-header">
                    <AlertCircle size={20} />
                    <span>{validationErrors.length} erro(s) encontrado(s)</span>
                  </div>
                  <ul className="errors-list">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mapping-grid">
                <div className="mapping-header">
                  <span>Campos</span>
                  <span>Equivalências</span>
                </div>

                {PAYLOAD_FIELDS.map((field) => (
                  <div key={field.key} className="mapping-row">
                    <div className="field-label">
                      {field.label}
                      {field.required && <span className="required">*</span>}
                    </div>
                    <select
                      className="field-select"
                      value={columnMapping[field.key] || ''}
                      onChange={(e) => setColumnMapping({ ...columnMapping, [field.key]: e.target.value })}
                    >
                      <option value="">Selecione uma coluna</option>
                      {sheetColumns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                      {!field.required && <option value="_ignore">Ignorar esse campo</option>}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="import-preview-section">
              <div className="preview-info">
                <p><strong>Tipo:</strong> {dataType}</p>
                <p><strong>Total de registros:</strong> {previewData.length}</p>
              </div>

              <div className="import-preview-table">
                <h4 className="preview-title">Pré-visualização dos dados mapeados</h4>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        {PAYLOAD_FIELDS.filter(f => columnMapping[f.key]).map((field) => (
                          <th key={field.key}>{field.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 5).map((row, index) => (
                        <tr key={index}>
                          {PAYLOAD_FIELDS.filter(f => columnMapping[f.key]).map((field) => {
                            const colName = columnMapping[field.key];
                            return (
                              <td key={field.key}>
                                {colName ? String(row[colName] || '') : ''}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {previewData.length > 5 && (
                  <p className="preview-note">Mostrando 5 de {previewData.length} registros</p>
                )}
              </div>
            </div>
          )}

          {step === 'error-report' && (
            <div className="error-report-section">
              <div className="error-stats">
                <div className="stat-card stat-total">
                  <FileSpreadsheet size={32} />
                  <div>
                    <p className="stat-value">{importStats?.total || 0}</p>
                    <p className="stat-label">Total de linhas</p>
                  </div>
                </div>
                <div className="stat-card stat-success">
                  <CheckCircle2 size={32} />
                  <div>
                    <p className="stat-value">{importStats?.success || 0}</p>
                    <p className="stat-label">Linhas válidas</p>
                  </div>
                </div>
                <div className="stat-card stat-error">
                  <AlertCircle size={32} />
                  <div>
                    <p className="stat-value">{importStats?.errors || 0}</p>
                    <p className="stat-label">Linhas com erro</p>
                  </div>
                </div>
              </div>

              <div className="error-details">
                <div className="error-details-header">
                  <h4>Detalhes dos Erros</h4>
                  <button className="btn-download-report" onClick={handleDownloadErrorReport}>
                    <Upload size={16} />
                    Baixar Relatório
                  </button>
                </div>

                <div className="error-list">
                  {rowErrors.map((error, index) => (
                    <div key={index} className="error-item">
                      <div className="error-item-header">
                        <span className="error-line-number">Linha {error.line}</span>
                        <span className="error-count">{error.errors.length} erro(s)</span>
                      </div>
                      <ul className="error-messages">
                        {error.errors.map((err, idx) => (
                          <li key={idx}>
                            <AlertCircle size={16} />
                            {err}
                          </li>
                        ))}
                      </ul>
                      {error.data && (
                        <details className="error-data-details">
                          <summary>Ver dados da linha</summary>
                          <pre>{JSON.stringify(error.data, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>

                <div className="error-actions-info">
                  <AlertCircle size={20} />
                  <p>Você pode corrigir os erros na planilha e tentar novamente, ou importar apenas as linhas válidas.</p>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {step !== 'success' && (
        <div className="import-modal-footer">
          <button className="btn-modal-cancel" onClick={handleClose} disabled={isProcessing}>
            Cancelar
          </button>
          
          {step === 'configure' && (
            <button
              className="btn-modal-next"
              onClick={handleConfigure}
              disabled={isProcessing}
            >
              Avançar
            </button>
          )}
          
          {step === 'preview' && (
            <button
              className="btn-modal-import"
              onClick={handleImport}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader size={18} className="spinner" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Importar Dados
                </>
              )}
            </button>
          )}

          {step === 'error-report' && (
            <>
              <button className="btn-modal-back" onClick={handleBack} disabled={isProcessing}>
                Voltar e Corrigir
              </button>
              <button
                className="btn-modal-import-partial"
                onClick={handleIgnoreErrorsAndImport}
                disabled={isProcessing || (importStats?.success || 0) === 0}
              >
                {isProcessing ? (
                  <>
                    <Loader size={18} className="spinner" />
                    Processando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} />
                    Importar {importStats?.success || 0} linha(s) válida(s)
                  </>
                )}
              </button>
            </>
          )}
        </div>
        )}

        {confirmOpen && (
          <div className="confirm-overlay">
            <div className="confirm-popup">
              <h3>Tem certeza?</h3>
              <p>
                Você está prestes a alterar o tipo de envio de <strong>{dataType}</strong> para <strong>{pendingType}</strong>.
              </p>
              <p>
                {pendingType === 'INCREMENTAL' ? (
                  <>INCREMENTAL: Os novos registros serão <strong>somados</strong> aos registros anteriores.</>
                ) : (
                  <>CONSOLIDADO: Os registros anteriores serão <strong>apagados</strong> e substituídos pelos novos.</>
                )}
              </p>
              <div className="confirm-actions">
                <button className="btn-cancel" onClick={confirmChangeCancel}>Cancelar</button>
                <button className="btn-confirm" onClick={confirmChangeApply}>Confirmar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
